import { createFileRoute } from "@tanstack/react-router"
import {
  CalendarDays,
  CircleHelp,
  Download,
  FileSpreadsheet,
  Flag,
  Route as RouteIcon,
} from "lucide-react"

import ogImage from "@/core/assets/og.png"
import { articleJsonLd, breadcrumbJsonLd, buildHead } from "@/core/seo"

import {
  AnswerBox,
  Breadcrumb,
  CtaBand,
  FaqAccordion,
  RelatedLinks,
  SimpleTable,
  SourceLine,
} from "../../components/landing"
import { HeroBand, RouteSteps, SectionTitle } from "../../components/landing/page-parts"
import { IconTile, LogbookIllustration } from "../../components/landing/product-art"
import { ATO_LOGBOOK } from "../../content/pages/faq-items"
import { formatDateZh } from "../../content/zh/dates"
import { COLUMNS, EXAMPLE_ROWS, REVIEWED_ISO } from "../ato-logbook-template"

/**
 * Chinese version of /ato-logbook-template (D-WEB-LANG-SCOPE, T1322); the English route carries the
 * hreflang pair too. The downloadable CSV and PDF stay English files (the same files as the English
 * page). Example numbers come from the English page's rows; the review date is the English page's.
 */
const PATH = "/ato-logbook-template"
const checked = formatDateZh(REVIEWED_ISO)
const ATO_SOURCE = { label: "ATO 对 logbook 法的说明", href: ATO_LOGBOOK.href }

export const logbookTemplateZhSeo = () =>
  buildHead({
    title: "ATO 行车日志模板（CSV/PDF）",
    description:
      "免费下载车辆行车日志模板：CSV 可用 Excel 或 Google Sheets 填写，PDF 可打印。附填写示例和需要记录的内容，无需注册。",
    path: PATH,
    localePrefix: "zh",
    jsonLd: [
      articleJsonLd({
        headline: "免费 ATO 行车日志模板（CSV 和 PDF）",
        path: PATH,
        localePrefix: "zh",
        image: ogImage,
        datePublished: REVIEWED_ISO,
        dateModified: REVIEWED_ISO,
      }),
      breadcrumbJsonLd([{ name: "首页", path: "/zh" }, { name: "行车日志模板" }]),
    ],
  })

export const Route = createFileRoute("/_brand-layout/zh/ato-logbook-template")({
  head: () => {
    const { meta, links, scripts } = logbookTemplateZhSeo()
    return { meta, links, scripts }
  },
  component: LogbookTemplateZhPage,
})

/** Column names as the Chinese reader sees them in the example table; the file's own (English) names are listed apart. */
const COLUMNS_ZH = ["日期", "起始里程", "结束里程", "公里数", "目的", "用途"]

/** Fictional trips; the numbers are the English page's, only the words are translated. */
const EXAMPLE_TEXT_ZH: readonly (readonly [string, string, string])[] = [
  ["9月14日（周一）", "到客户现场报价", "业务"],
  ["9月15日（周二）", "到供应商取配件", "业务"],
  ["9月15日（周二）", "回家", "私人"],
]
const EXAMPLE_ROWS_ZH = EXAMPLE_ROWS.map(([, start, end, km], i) => {
  const [date, purpose, use] = EXAMPLE_TEXT_ZH[i]!
  return { header: date, cells: [start!, end!, km!, purpose, use] }
})

const STEPS = [
  { title: "第一天之前", body: "写下开始日期和里程表读数。", icon: CalendarDays },
  {
    title: "每一次业务行程",
    body: "日期、起始和结束里程表读数、公里数，以及清楚的目的。",
    icon: RouteIcon,
  },
  {
    title: "最后一天",
    body: "写下结束日期和里程表读数，再算出业务用途的百分比。",
    icon: Flag,
  },
]

const FAQ = [
  {
    question: "用电子表格做 logbook 可以吗？",
    answer: "ATO 要求你在这段期间保存规定的各项内容，没有规定要用哪种工具。",
  },
  {
    question: "可以事后再补填吗？",
    answer: "请边开边记。凭记忆补出来的行车日志，更难让人信服。",
  },
]

const downloadClass =
  "bg-primary text-primary-foreground focus-visible:ring-ring inline-flex min-h-11 items-center rounded-md px-5 font-semibold outline-none focus-visible:ring-2"

export function LogbookTemplateZhPage() {
  return (
    <>
      <Breadcrumb
        items={[{ label: "首页", href: "/" }, { label: "指南" }, { label: "行车日志模板" }]}
      />
      <HeroBand>
        <p className="text-muted-foreground text-sm">
          免费模板 · 不是 ATO 的表格 · 最近复核 {checked}
        </p>
        <h1 className="text-3xl font-bold tracking-tight md:text-5xl">
          免费 ATO 行车日志模板（CSV 和 PDF）
        </h1>
        <div className="max-w-[70ch]">
          <AnswerBox source={<SourceLine sources={[ATO_SOURCE]} checked={checked} />}>
            下载 CSV，用 Excel 或 Google Sheets 填写；或者下载 PDF 打印出来填。CSV
            每趟行程一行，列出日期、起始和结束里程表读数、公里数、目的，以及是业务还是私人用途；PDF
            另外有 12 周开始和结束的填写框。你必须记录哪些内容，请看 ATO 的 logbook 法页面。
          </AnswerBox>
        </div>
      </HeroBand>

      <section className="px-band grid gap-4 py-8" aria-labelledby="download-title">
        <SectionTitle id="download-title" icon={Download} tone="primary">
          下载模板
        </SectionTitle>
        <div className="flex flex-wrap gap-3">
          <a href="/templates/kestly-logbook-template.csv" download className={downloadClass}>
            CSV（Excel、Google Sheets）
          </a>
          <a href="/templates/kestly-logbook-template.pdf" download className={downloadClass}>
            PDF（用于打印）
          </a>
        </div>
        <div className="border-border bg-card grid max-w-[70ch] gap-3 rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <IconTile icon={FileSpreadsheet} tone="success" />
            <div>
              <b className="block">每趟行程的列</b>
              <span className="text-muted-foreground text-sm">CSV 文件的第一行</span>
            </div>
          </div>
          <ol
            aria-label="CSV 文件的列"
            lang="en"
            className="flex flex-wrap gap-2 font-mono text-sm"
          >
            {COLUMNS.map((c) => (
              <li key={c} className="bg-muted text-foreground rounded-md px-2.5 py-1">
                {c}
              </li>
            ))}
          </ol>
          <p className="text-muted-foreground text-sm">
            文件里的列名是英文，依次是：{COLUMNS_ZH.join("、")}。
          </p>
        </div>
        <figure className="grid max-w-[420px] gap-2">
          <LogbookIllustration range="10月5–11日" />
          <figcaption className="text-muted-foreground text-center text-sm">
            示例：12 周的行车日志，按周逐周填写
          </figcaption>
        </figure>
        <p className="text-muted-foreground max-w-[70ch]">
          无需注册。这份模板不是 ATO 发布或认可的表格，只是一个版式；你必须记录什么，以 ATO
          的规定为准。
        </p>
      </section>

      <section className="px-band grid gap-4 py-8" aria-labelledby="example-title">
        <SectionTitle id="example-title" icon={FileSpreadsheet} tone="success">
          行车日志示例
        </SectionTitle>
        <p className="max-w-[70ch]">
          下面是行车日志里的三行示例。你的行车日志是你自己保存的记录，请填你自己的行程。
        </p>
        <SimpleTable
          caption="行车日志示例（虚构行程）"
          columns={COLUMNS_ZH}
          rows={EXAMPLE_ROWS_ZH}
        />
      </section>

      <section className="px-band grid gap-4 py-8" aria-labelledby="steps-title">
        <SectionTitle id="steps-title" icon={RouteIcon} tone="warning">
          怎么填
        </SectionTitle>
        <RouteSteps steps={STEPS} />
      </section>

      <section className="px-band grid gap-4 py-8" aria-labelledby="faq-title">
        <SectionTitle id="faq-title" icon={CircleHelp} tone="success">
          常见问题
        </SectionTitle>
        <FaqAccordion items={FAQ} />
        <SourceLine sources={[ATO_SOURCE]} checked={checked} />
        <p className="text-muted-foreground max-w-[70ch] text-sm">
          仅为一般性信息，不是税务建议；请向 ATO 或注册税务代理核实你的具体情况。
        </p>
      </section>

      <RelatedLinks
        links={[
          { label: "里程记录 App：自动 GPS 行车日志", href: "/mileage-tracker" },
          {
            label: "ATO 对 logbook 的要求：要记录什么（英文）",
            href: "/guides/ato-logbook-requirements",
          },
          { label: "logbook 法：什么时候划算（英文）", href: "/logbook-method" },
          { label: "cents per km 费率和计算器", href: "/cents-per-km-calculator" },
        ]}
      />

      <CtaBand
        title="让手机替你记录"
        description="GPS 行程、每趟的原因，12 周结束时导出 CSV 行程记录。"
        ctaLabel="开始免费试用"
        ctaHref="/login"
      />
    </>
  )
}
